import React from 'react'
import AuditActions from './AuditActions'
import {Audit, AuditDocument} from '../types/audit.types'
import {Status} from './Status'
import FormatTimestamp from 'app/components/FormatTimestamp'
// import Overlay from 'app/components/Overlay'
import Button from 'app/components/Button'

interface AuditCardProps {
  audit: Audit
  currentUser: any
  downloadLoading: boolean
  selectedAuditId: string
  selectedFileName?: {[key: string]: string}
  onEditAudit?: (auditId: string) => void
  onUploadReport?: (auditId: string) => void
  onMarkComplete?: (auditId: string, audit: Audit) => void
  onDownloadAuditReport: (auditId: string, auditDocument: AuditDocument) => void
  onDownloadReport?: (auditId: string, dataroomFolderId: string, folderName: string) => void
  onDelete?: (audit: Audit) => void
  onToggleDropdown?: () => void
  onView?: (auditDocument: AuditDocument) => void
  handleOpenAudit?: (audit: Audit) => void
  currentAuditContext?: any
  onEditAuditors?: (auditId: string) => void
}

const AuditCard: React.FC<AuditCardProps> = ({
  audit,
  currentUser,
  downloadLoading,
  selectedAuditId,
  selectedFileName,
  onEditAudit,
  onUploadReport,
  onMarkComplete = () => {},
  onDownloadAuditReport,
  onDownloadReport,
  onDelete,
  onView,
  handleOpenAudit,
  currentAuditContext,
  onEditAuditors,
}) => {
  const getFrameworkIcon = (framework: string) => {
    switch (framework) {
      case 'ISO 27001:2022':
        return '/media/framework/ISO27001.svg'
      case 'SOC 2':
        return '/media/framework/SOC2.svg'
      case 'GDPR':
        return '/media/framework/GDPR.svg'
      case 'HIPAA':
        return '/media/framework/HIPAA.svg'
      default:
        return '/media/icon/File-bg.svg'
    }
  }

  const isFutureDate = audit?.start_date
    ? new Date(audit.start_date).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)
    : false

  const computedStatus =
    audit.status === 'Completed'
      ? 'Completed'
      : audit.end_date && new Date(audit.end_date) < new Date()
      ? 'Due'
      : audit.start_date && new Date(audit.start_date) > new Date()
      ? 'upcoming'
      : audit.status

  return (
    <div
      className='card d-flex align-items-center flex-row  mb-4'
      style={{
        border: '1px solid #F1F3F9',
        borderRadius: '8px',
        boxShadow: 'none',
        background: '#fff',
        minHeight: '80px',
        justifyContent: 'space-between',
        padding: '24px',
      }}
    >
      <div
        className='d-flex align-items-center me-5'
        style={{
          width: '45px',
          height: '45px',
          overflow: 'hidden',
          backgroundColor: '#F6F6F6',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={getFrameworkIcon(audit.framework[0])}
          alt=''
          loading='lazy'
          style={{
            display: 'block',
            width: '50%',
            height: '50%',
            objectFit: 'contain',
          }}
        />
      </div>

      <div className='flex-grow-1 d-flex flex-column justify-content-center' style={{minWidth: 0}}>
        <div
          className='fw-semibold fs-5 mb-1'
          style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}
        >
          {`${audit.auditTitle ? audit.auditTitle : audit.framework[0] + ' Audit'} `}{' '}
          {/* {currentUser.role !== 'Auditor' && computedStatus === 'Due' && (
            <Overlay
              content="Auditor access has expired as the due date has passed. To resume access, please extend the due date or complete the audit."
              placement='right'
            />
          )} */}
        </div>
        <div className='d-flex align-items-center flex-wrap gap-2 fs-7 text-gray-900'>
          {audit.auditor ? (
            <span>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                stroke='oklch(21% 0.034 264.665)'
                stroke-width='2'
                stroke-linecap='round'
                stroke-linejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-briefcase mb-1  me-1'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M3 7m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z' />
                <path d='M8 7v-2a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v2' />
                <path d='M12 12l0 .01' />
                <path d='M3 13a20 20 0 0 0 18 0' />
              </svg>{' '}
              {audit?.auditorOrganization || ''}
            </span>
          ) : (
            <span>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                stroke-width='2'
                stroke-linecap='round'
                stroke-linejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-external-link mb-1 me-1›'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6' />
                <path d='M11 13l9 -9' />
                <path d='M15 4h5v5' />
              </svg>{' '}
              Via Sharing Externally
            </span>
          )}
          <span>·</span>
          <span>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='26'
              viewBox='0 0 24 24'
              fill='none'
              stroke='oklch(21% 0.034 264.665)'
              stroke-width='2'
              stroke-linecap='round'
              stroke-linejoin='round'
              className='icon icon-tabler icons-tabler-outline icon-tabler-box mb-1 me-1'
            >
              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
              <path d='M12 3l8 4.5l0 9l-8 4.5l-8 -4.5l0 -9l8 -4.5' />
              <path d='M12 12l8 -4.5' />
              <path d='M12 12l0 9' />
              <path d='M12 12l-8 -4.5' />
            </svg>{' '}
            {audit.framework[0]}
          </span>
          <span>·</span>
          <span>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 24 24'
              fill='none'
              stroke='oklch(21% 0.034 264.665)'
              stroke-width='2'
              stroke-linecap='round'
              stroke-linejoin='round'
              className='icon icon-tabler icons-tabler-outline icon-tabler-calendar-event mb-1'
            >
              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
              <path d='M4 5m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z' />
              <path d='M16 3l0 4' />
              <path d='M8 3l0 4' />
              <path d='M4 11l16 0' />
              <path d='M8 15h2v2h-2z' />
            </svg>
            <span className='ms-2'>
              {FormatTimestamp({timestamp: audit.start_date})} -{' '}
              {FormatTimestamp({timestamp: audit.end_date})}
            </span>
          </span>
          <span>·</span>
          <span>
            <Status status={computedStatus} />
          </span>
        </div>
      </div>
      {audit.status === 'Completed' ? (
        <>
          <Button
            btnType='default'
            style={{
              height: '34px',
            }}
            onClick={() =>
              onView &&
              audit.audit_documents &&
              audit.audit_documents.length > 0 &&
              onView(audit.audit_documents[0])
            }
          >
            View Report
          </Button>
        </>
      ) : (
        <div className='d-flex align-items-center ms-3' style={{gap: '8px'}}>
          {(!currentAuditContext && currentUser?.role !== 'Auditor')  && (
              <Button
              btnType='default'
              onClick={() => handleOpenAudit && handleOpenAudit(audit)}
              style={{
                height: '34px',
              }}
            >
              View Audit
            </Button>
          )}
          <Button
            btnType='default'
            onClick={() => onMarkComplete(audit.id, audit)}
            disabled={isFutureDate || currentAuditContext}
            style={{
              height: '34px',
            }}
          >
            Complete Audit
          </Button>
          <AuditActions
            audit={audit}
            currentUserRole={currentUser?.role}
            downloadLoading={downloadLoading}
            selectedAuditId={selectedAuditId}
            onEditAudit={onEditAudit || (() => {})}
            onUploadReport={onUploadReport || (() => {})}
            onMarkComplete={onMarkComplete || (() => {})}
            onDownloadAuditReport={onDownloadAuditReport || (() => {})}
            onDownloadReport={onDownloadReport || (() => {})}
            onDelete={onDelete || (() => {})}
            disabled={isFutureDate && currentUser?.role === 'Auditor'}
            currentAuditContext={currentAuditContext}
            isFutureDate={isFutureDate}
            onEditAuditors={onEditAuditors || (() => {})}
          />
        </div>
      )}
    </div>
  )
}

export default AuditCard
